import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Share2, Loader2, Copy, Check, MoreVertical, Image, Flag, Eraser } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { GeneratedImage } from './ImagesGrid';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { getCloudinaryPreviewUrl } from '@/utils/cloudinary';
import { SendEmailService } from '@/services/SendEmailService';

interface FullscreenImageModalProps {
  image: GeneratedImage | null;
  isLoading: boolean;
  onClose: () => void;
  onDownload?: (url: string, prompt?: string) => Promise<void>;
  isDarkMode?: boolean;
  onUseImage?: (dataUrl: string) => void;
}

export const FullscreenImageModal: React.FC<FullscreenImageModalProps> = ({ image, isLoading, onClose, onDownload, isDarkMode = true, onUseImage }) => {
  if (!image) return null;

  const attachedUrls = (() => {
    try {
      const raw: any = (image as any).attached_media;
      if (Array.isArray(raw)) return raw.filter((u) => typeof u === 'string' && u.trim().length > 0);
      if (typeof raw === 'string') {
        const s = raw.trim();
        if (!s) return [];
        if (s.startsWith('[')) {
          const parsed = JSON.parse(s);
          return Array.isArray(parsed) ? parsed.filter((u) => typeof u === 'string' && u.trim().length > 0) : [];
        }
        return [s];
      }
      return [];
    } catch {
      return [];
    }
  })();

  useEffect(() => {
    const { overflow, touchAction } = document.body.style;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.touchAction = touchAction;
    };
  }, []);

  const [isDownloadHovered, setIsDownloadHovered] = useState(false);
  const [isShareHovered, setIsShareHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [isUsingImage, setIsUsingImage] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string, index: number } | null>(null);
  const [showPromptDetails, setShowPromptDetails] = useState(false);
  const promptDetailsRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const [likeCount, setLikeCount] = useState<number>(image.likes_count ?? 0);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [popping, setPopping] = useState(false);

  const handleBackdropClick = () => {
    if (showPromptDetails) {
      setShowPromptDetails(false);
      return;
    }
    onClose();
  };

  useEffect(() => {
    if (!showPromptDetails) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = promptDetailsRef.current;
      if (!el) return;
      if (el.contains(e.target as Node)) return;
      setShowPromptDetails(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [showPromptDetails]);

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getUser();
      const userId = data?.user?.id;
      if (!userId || !image.id) return;
      const { data: rows, error } = await supabase
        .from('image_likes')
        .select('image_id')
        .eq('user_id', userId)
        .eq('image_id', image.id)
        .limit(1);
      if (!error) setIsLiked((rows?.length || 0) > 0);
    };
    init();
  }, [image?.id]);

  useEffect(() => {
    // Reset any processed image when a new image is viewed
    setProcessedUrl(null);
    setIsRemovingBg(false);
  }, [image?.id]);

  const toggleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!image.id) return;
    const { data } = await supabase.auth.getUser();
    const userId = data?.user?.id;
    if (!userId) {
      toast({ title: 'Sign in required', description: 'Please sign in to like images.' });
      try { window.dispatchEvent(new CustomEvent('openAuthModal')); } catch { }
      return;
    }
    if (image.user_id && image.user_id === userId) {
      return;
    }
    const nextLiked = !isLiked;
    setIsLiked(nextLiked);
    setLikeCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    setPopping(true);
    window.setTimeout(() => setPopping(false), 200);
    if (nextLiked) {
      const { error } = await supabase.from('image_likes').insert({ image_id: image.id, user_id: userId });
      if (error) console.error('Like error', error);
    } else {
      const { error } = await supabase.from('image_likes').delete().eq('image_id', image.id).eq('user_id', userId);
      if (error) console.error('Unlike error', error);
    }
  };

  const baseBackground = theme.bg(isDarkMode, { transparency: 0.9, dark: '#212121', light: '#ffffff' });
  const hoverBackground = theme.bg(isDarkMode, { transparency: 0.9, dark: '#2b2b2b', light: '#f1f1f1' });

  const buttonStyle = (hovered: boolean) => ({
    backgroundColor: hovered ? hoverBackground : baseBackground,
    color: theme.text(isDarkMode),
    borderColor: theme.border(isDarkMode, 0.1),
    borderWidth: 1,
    borderStyle: 'solid',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
  });

  const originalDisplayUrl = image.cloudinaryUrl || image.image_url || image.url;
  const displayUrl = processedUrl || originalDisplayUrl;
  const createdDate = image.createdAt ? new Date(image.createdAt) : null;
  const dateOnly = createdDate ? createdDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const dateTime = createdDate ? createdDate.toLocaleString() : '';

  const dataUrlToBlob = async (dataUrl: string) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const getBlobViaProxy = async (): Promise<{ blob: Blob; contentType: string; ext: string }> => {
    // If it's a data URL, no need to proxy
    if (displayUrl.startsWith('data:')) {
      const blob = await dataUrlToBlob(displayUrl);
      const contentType = blob.type || 'image/webp';
      const ext = contentType.includes('png') ? 'png' : contentType.includes('jpeg') ? 'jpg' : contentType.includes('jpg') ? 'jpg' : contentType.includes('gif') ? 'gif' : contentType.includes('webp') ? 'webp' : 'png';
      return { blob, contentType, ext };
    }

    // Use Supabase Edge Function to fetch image bytes (bypasses CSP connect-src)
    const { data, error } = await supabase.functions.invoke('proxy-image', {
      body: { url: displayUrl },
    });
    if (error || !data?.success) throw new Error(data?.error || 'Proxy fetch failed');
    const contentType: string = data.contentType || 'image/webp';
    const b64: string = data.data;
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    const ext = contentType.includes('png') ? 'png' : contentType.includes('jpeg') ? 'jpg' : contentType.includes('jpg') ? 'jpg' : contentType.includes('gif') ? 'gif' : contentType.includes('webp') ? 'webp' : 'png';
    return { blob, contentType, ext };
  };

  const handleDownload = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // Use prop-based downloader only for data URLs (CSP-safe)
      if (onDownload && displayUrl.startsWith('data:')) {
        await onDownload(displayUrl, image.prompt);
      } else {
        const { blob, ext } = await getBlobViaProxy();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safePrompt = (image.prompt || 'generated-image').replace(/[^a-z0-9]/gi, '_').toLowerCase();
        link.href = objectUrl;
        link.download = `chitra-ai-${safePrompt.slice(0, 20)}-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      // Fallback: open in new tab if download via blob failed
      const fallback = document.createElement('a');
      fallback.href = displayUrl;
      fallback.target = '_blank';
      fallback.rel = 'noopener noreferrer';
      document.body.appendChild(fallback);
      fallback.click();
      document.body.removeChild(fallback);
    }
    setIsDownloading(false);
  };

  const handleShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const shareText = image.prompt || 'Check out this image I created with Chitra AI!';
    if (isSharing) return;
    setIsSharing(true);
    try {
      // Get blob via proxy (CSP-safe) or from data URL
      const { blob, contentType, ext } = await getBlobViaProxy();
      const fileName = `chitra-ai-image.${ext}`;
      const file = new File([blob], fileName, { type: contentType });

      // Prefer sharing the actual file if supported
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'AI Generated Image', text: shareText, files: [file] });
        return;
      }

      // Fallback: share URL/text if files are not supported
      if (navigator.share) {
        await navigator.share({ title: 'AI Generated Image', text: shareText, url: displayUrl });
        return;
      }

      // Fallback 2: copy link or open in new tab
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(displayUrl);
      } else {
        window.open(displayUrl, '_blank', 'noopener');
      }
    } catch (error) {
      console.error('Error sharing image:', error);
    }
    setIsSharing(false);
  };

  const handleCopyPrompt = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!image.prompt) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(image.prompt);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = image.prompt;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setPromptCopied(true);
      window.setTimeout(() => setPromptCopied(false), 2000);
      setMenuOpen(false);
    } catch (error) {
      console.error('Error copying prompt:', error);
    }
  };

  const handleUseImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isUsingImage) return;
    setIsUsingImage(true);
    try {
      // Acquire blob via proxy helper (works for data URLs and remote URLs)
      const { blob } = await getBlobViaProxy();
      // Convert blob to data URL so it can be attached for edit flow
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        if (dataUrl && onUseImage) {
          onUseImage(dataUrl);
        }
        setIsUsingImage(false);
        setMenuOpen(false);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error preparing image for use:', err);
      setIsUsingImage(false);
    }
  };

  const handleReportImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isReporting) return;
    setIsReporting(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const reporterEmail = authData?.user?.email || 'Unknown';

      await SendEmailService.send({
        type: 'image_report',
        userEmail: reporterEmail,
        data: {
          displayUrl,
          prompt: image.prompt || '(none)',
          category: 'Image Violation/Inappropriate',
          triggerSource: 'Chitra AI Fullscreen'
        },
        clientInfo: {
          userAgent: navigator.userAgent,
          platform: (navigator as any).platform || 'unknown',
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          referrer: document.referrer || 'direct',
        }
      });

      toast({ title: "Report Sent", description: "" });
      setMenuOpen(false);
    } catch (err: any) {
      console.error('Error reporting image:', err);
      toast({ title: "Error", description: err.message || "Failed to send report.", variant: "destructive" });
    }
    setIsReporting(false);
  };

  const handleRemoveBackground = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isRemovingBg) return;
    setIsRemovingBg(true);
    try {
      const { blob } = await getBlobViaProxy();
      const form = new FormData();
      form.append('image_file', blob, 'image.png');
      // form.append('transparency_handling', 'return_input_if_non_opaque'); // optional

      const res = await fetch('https://clipdrop-api.co/remove-background/v1', {
        method: 'POST',
        headers: {
          'x-api-key': 'f6944e346a805517fc021347181c4f098e012d7e70583549a0b07e43f5584f851fb1c10333409394e1a31d4a5e8488d4',
          'accept': 'image/png',
        },
        body: form,
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok || contentType.includes('application/json')) {
        let message = 'Background removal failed';
        try { const j = await res.json(); message = j?.error || message; } catch { }
        toast({ title: 'Remove BG error', description: message });
        setIsRemovingBg(false);
        return;
      }

      const outBlob = await res.blob();
      const objectUrl = URL.createObjectURL(outBlob);
      setProcessedUrl(objectUrl);
      setMenuOpen(false);
    } catch (err) {
      console.error('Remove BG error', err);
      toast({ title: 'Remove BG error', description: 'Something went wrong. Please try again.' });
    }
    setIsRemovingBg(false);
  };

  return (
    <div
      className="fixed inset-0 max-w-[100vw] max-h-[100vh] z-[99999] flex flex-col"
      style={{ backgroundColor: isDarkMode ? '#303030' : 'white' }}
      onClick={handleBackdropClick}
    >
      {/* Top header */}
      <div
        className="flex items-center justify-between px-4 sm:px-6 py-3"
        style={{ color: theme.text(isDarkMode) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Creator info if available */}
          {image.creator_avatar && (
            <img src={image.creator_avatar} alt="creator" className="h-7 w-7 rounded-full object-cover shadow-sm" />
          )}
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 text-sm opacity-80 truncate">
              <span className="truncate max-w-[40vw] sm:max-w-none">{image.creator_name || 'OREOG'}</span>
              <span className="opacity-50 hidden">•</span>
              <span className="truncate hidden">{image.style || 'Image'}</span>
            </div>
            {image.createdAt && (
              <>
                <span className="text-xs opacity-60 truncate">{dateOnly}</span>
                {/* <span className="text-xs opacity-60 truncate hidden sm:inline">{dateTime}</span> */}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLike}
            className="flex items-center gap-2 h-9 px-3 rounded-full transition-transform duration-200"
            style={{
              color: isLiked ? (isDarkMode ? '#fda4af' : '#e11d48') : theme.text(isDarkMode),
              backgroundColor: baseBackground,
              border: `1px solid ${theme.border(isDarkMode, 0.1)}`,
            }}
          >
            <FontAwesomeIcon
              icon={isLiked ? faHeartSolid : faHeartRegular}
              className="h-4 w-4 transition-transform duration-200"
              style={{ transform: popping ? 'scale(1.15)' : 'scale(1)' }}
            />
            <span>{likeCount}</span>
          </button>
          <Button
            onClick={handleDownload}
            onMouseEnter={() => setIsDownloadHovered(true)}
            onMouseLeave={() => setIsDownloadHovered(false)}
            className="rounded-full h-9 px-3 hidden sm:inline-flex"
            style={buttonStyle(isDownloadHovered)}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Download</>
            ) : (
              <><Download className="w-4 h-4 mr-2" />Download</>
            )}
          </Button>

          <div className="relative">
            <Button
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              className="rounded-full h-9 w-9 p-0"
              style={buttonStyle(isMenuHovered)}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-44 rounded-xl shadow-lg z-[10000]"
                style={{ backgroundColor: baseBackground, border: `1px solid ${theme.border(isDarkMode, 0.1)}` }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Mobile-only actions: Download & Share */}
                <button
                  className="sm:hidden w-full text-left px-3 py-2 hover:bg-white/10 dark:hover:bg-black/10 flex items-center gap-2 rounded-t-xl"
                  style={{ color: theme.text(isDarkMode) }}
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Download</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </>
                  )}
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-white/10 rounded-t-xl dark:hover:bg-black/10 flex items-center gap-2"
                  style={{ color: theme.text(isDarkMode) }}
                  onClick={handleShare}
                  disabled={isSharing}
                >
                  {isSharing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sharing...</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      <span>Share</span>
                    </>
                  )}
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-white/10 dark:hover:bg-black/10 flex items-center gap-2"
                  style={{ color: theme.text(isDarkMode) }}
                  onClick={handleCopyPrompt}
                  disabled={!image.prompt}
                >
                  {promptCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>Copy prompt</span>
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-white/10 dark:hover:bg-black/10 flex items-center gap-2"
                  style={{ color: theme.text(isDarkMode) }}
                  onClick={handleUseImage}
                  disabled={isUsingImage}
                >
                  {isUsingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Use image</span>
                    </>
                  ) : (
                    <>
                      <Image className="w-4 h-4" />
                      <span>Use image</span>
                    </>
                  )}
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-white/10 dark:hover:bg-black/10 flex items-center gap-2 rounded-b-xl text-red-600 dark:text-red-400 disabled:opacity-60"
                  onClick={handleReportImage}
                  disabled={isReporting}
                >
                  {isReporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                  <span>{isReporting ? 'Reporting...' : 'Report image'}</span>
                </button>

                {/* Attached images thumbnails */}
                {attachedUrls.length > 0 && (
                  <div className="px-2 pt-2 border-t" style={{ borderColor: theme.border(isDarkMode, 0.08) }}>
                    <div className="text-xs mb-2 opacity-70" style={{ color: theme.text(isDarkMode) }}>Image Attachments</div>
                    <div className="grid grid-cols-3 gap-2">
                      {attachedUrls.slice(0, 9).map((u, i) => (
                        <div key={i} className="relative group">
                          <button
                            className="relative w-full aspect-square rounded overflow-hidden hover:opacity-90"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage({ url: u, index: i });
                            }}
                            title="Open"
                            aria-label="Attached image"
                          >
                            <img src={getCloudinaryPreviewUrl(u, 90)} alt={`attached-${i}`} className="w-full h-full object-cover" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-full h-9 w-9 flex items-center justify-center"
            style={{ color: theme.text(isDarkMode), backgroundColor: baseBackground, border: `1px solid ${theme.border(isDarkMode, 0.1)}` }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 w-full flex items-center justify-center px-2 sm:px-6" onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <div className="flex items-center justify-center w-96 h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.text(isDarkMode) }} />
          </div>
        ) : (
          <img
            src={displayUrl}
            alt={image.prompt || 'Fullscreen image'}
            className="max-w-[92vw] max-h-[82vh] object-contain"
          />
        )}
      </div>

      {/* Bottom prompt/info bar */}
      <div className="w-full text-center px-4 sm:px-6 py-3 overflow-visible">
        <div
          className="relative mx-auto max-w-4xl w-full rounded-xl overflow-visible bg-transparent"
          onClick={(e) => {
            e.stopPropagation();
            if (showPromptDetails) setShowPromptDetails(false);
          }}
        >
          {image.prompt && (
            <div
              className="relative"
            >
              <button
                type="button"
                className="w-full text-center text-sm truncate whitespace-nowrap overflow-hidden text-ellipsis px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPromptDetails((v) => !v);
                }}
              >
                <span className="font-bold text-[#9f9f9f]">
                  Prompt : </span>{image.prompt}
              </button>

              {showPromptDetails && (
                <div className="pointer-events-none rounded-2xl absolute bottom-full left-1/2 z-[999999999] mb-3 w-[min(100%,32rem)] -translate-x-1/2 px-3 max-h-[80vh] overflow-y-auto">
                  <div
                    ref={promptDetailsRef}
                    className="pointer-events-auto rounded-2xl px-4 py-3 shadow-xl border backdrop-blur-sm"
                    style={{
                      backgroundColor: theme.bg(isDarkMode, { transparency: 0.85, dark: '#111111', light: '#ffffff' }),
                      color: theme.text(isDarkMode),
                      borderColor: theme.border(isDarkMode, 0.18),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words mb-2">{image.prompt}</p>
                    <div className="flex flex-wrap gap-3 text-xs justify-center opacity-80">
                      {image.style && (
                        <span>
                          <span className="font-medium">{image.style}</span>
                        </span>
                      )}
                      {image.aspect_ratio && (
                        <span>
                          <span className="font-medium">{image.aspect_ratio}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className="flex justify-center gap-4 text-xs px-3 pb-3"
            style={{ color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}
          >
          </div>
        </div>
      </div>
      {/* Preview Modal for Attached Images */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[999999] bg-black/90 flex items-center backdrop-blur-sm justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>

            <div className="relative">
              <img
                src={previewImage.url}
                alt={`preview-${previewImage.index}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute top-0 -right-0 text-white backdrop-blur-sm hover:bg-white/20 p-2 rounded-full"
                aria-label="Close preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullscreenImageModal;



